// Discord activities can't fetch external hosts directly. Mapillary-JS hard-codes
// graph.mapillary.com / tiles.mapillary.com / *.fbcdn.net regardless of urlOptions
// (v4 strips path components from apiHost), so we install global fetch + Image +
// XHR shims that rewrite every Mapillary URL to our same-origin nginx proxy paths.
//
// Must run BEFORE mapillary-js loads.

function rewrite(url: string): string {
  if (typeof url !== 'string' || !url) return url;
  const origin = window.location.origin;
  // graph.mapillary.com → /mly-graph
  let out = url.replace(/^https?:\/\/graph\.mapillary\.com/, `${origin}/mly-graph`);
  // tiles.mapillary.com → /mly-tiles
  out = out.replace(/^https?:\/\/tiles\.mapillary\.com/, `${origin}/mly-tiles`);
  // any *.fbcdn.net (image CDN) → /mly-cdn/<host>
  out = out.replace(/^https?:\/\/([\w.-]+\.fbcdn\.net)/, `${origin}/mly-cdn/$1`);
  // any *.cdninstagram.com (Mapillary sometimes uses these too)
  out = out.replace(/^https?:\/\/([\w.-]+\.cdninstagram\.com)/, `${origin}/mly-cdn/$1`);
  return out;
}

export function installMapillaryUrlRewriter(): void {
  // 1) fetch
  const origFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    if (typeof input === 'string') {
      return origFetch(rewrite(input), init);
    }
    if (input instanceof URL) {
      return origFetch(rewrite(input.toString()), init);
    }
    if (input instanceof Request) {
      const newUrl = rewrite(input.url);
      if (newUrl !== input.url) {
        return origFetch(new Request(newUrl, input), init);
      }
    }
    return origFetch(input, init);
  }) as typeof window.fetch;

  // 2) XMLHttpRequest
  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ) {
    const u = typeof url === 'string' ? rewrite(url) : rewrite(url.toString());
    // @ts-expect-error spread args
    return origOpen.call(this, method, u, ...rest);
  };

  // 3) HTMLImageElement.src setter — for tile loaders that bypass fetch/XHR
  const proto = HTMLImageElement.prototype;
  const orig = Object.getOwnPropertyDescriptor(proto, 'src');
  if (orig && orig.set) {
    Object.defineProperty(proto, 'src', {
      configurable: true,
      enumerable: true,
      get: orig.get,
      set(value: string) {
        orig.set!.call(this, rewrite(value));
      },
    });
  }
}
